import classNames from 'classnames';
import React, { useState } from 'react';
import formatHistoryDate from './formatHistoryDate';
import * as styles from './VersionChangeCard.module.scss';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import PopOver from '../../components/deprecated/PopOver';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';

interface RootProps {
  children: React.ReactNode;
  focused: boolean;
  onClick?: React.MouseEventHandler;
}

const Root = ({ children, focused, onClick }: RootProps) => {
  return (
    <div
      className={classNames(styles.root, {
        [styles.focused]: focused,
      })}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

interface ContentContainerProps {
  children: React.ReactNode;
}

const ContentContainer = ({ children }: ContentContainerProps) => {
  return <div className={styles.contentContainer}>{children}</div>;
};

interface PopOverRootProps {
  children: React.ReactNode;
}

const PopOverRoot = ({ children }: PopOverRootProps) => {
  return (
    <div className={styles.popOverRoot}>
      <PopOver.Container noPadding noMargin width="auto">
        <div className={styles.popOverRootInner}>{children}</div>
      </PopOver.Container>
    </div>
  );
};

interface NewBadgeContainerProps {
  children: React.ReactNode;
  showBadge: boolean;
}

const NewBadgeContainer = ({ children, showBadge }: NewBadgeContainerProps) => {
  return (
    <div className={styles.newBadgeContainer}>
      <div>{children}</div>
      {showBadge && (
        <Badge className={styles.badge} color="green">
          new
        </Badge>
      )}
    </div>
  );
};

interface ContentProps {
  name: string;
  insertedAt: Date;
}

const Content = ({ name, insertedAt }: ContentProps) => {
  return (
    <div className={styles.content}>
      <div className={styles.authorName}>{name}</div>
      <div className={styles.date}>{formatHistoryDate(insertedAt)}</div>
    </div>
  );
};

interface CompactAvatarContainerProps {
  children: React.ReactNode;
}

const CompactAvatarContainer = ({ children }: CompactAvatarContainerProps) => (
  <div className={styles.compactAvatarContainer}>{children}</div>
);

interface CopyLinkButtonProps {
  onCopyLink: () => void;
  children: React.ReactNode;
}

const CopyLinkButton = ({ children, onCopyLink }: CopyLinkButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);

  return (
    <Tooltip open={isCopied} content="Link copied">
      <Button
        className={styles.copyLinkButton}
        onMouseLeave={() => setIsCopied(false)}
        onBlur={() => setIsCopied(false)}
        onClick={() => {
          onCopyLink();
          setIsCopied(true);
        }}
      >
        {children}
        <div className={styles.copyLinkButtonIcon}>
          <SVGIcon>{linkIcon}</SVGIcon>
        </div>
      </Button>
    </Tooltip>
  );
};

export default {
  Root,
  PopOverRoot,
  NewBadgeContainer,
  Content,
  ContentContainer,
  CompactAvatarContainer,
  CopyLinkButton,
};
